import { Link as ScrollLink } from "react-scroll";
import { Page, Header, Section, Breadcrumb, FAQs, CallToAction, Markdown, BeforeAfter } from "@/components";
import { tinaClient, useTina, tinaField, cn } from "@/utils";
import { LogoEye } from "../../assets";

function hasMarkdownContent(content) {
  if (!content) return false;
  if (typeof content === "string") return content.trim().length > 0;
  if (typeof content.text === "string") return content.text.trim().length > 0;
  return content.children?.some(hasMarkdownContent) ?? false;
}

export default function Treatment({ treatmentQuery }) {
  const { data } = useTina({
    ...treatmentQuery,
  });

  const { title, header, overview, sections, beforeAfter, faqs, cta, seo } = data.treatment;

  return (
    <Page className="[&>section]:mt-20 lg:[&>section]:mt-24 [&>section:first-of-type]:mt-12 mb-24" seo={seo}>
      <Header
        header={{
          ...header,
          button: {
            label: sections?.[0]?.pretitle,
            link: `#${encodeURIComponent(sections?.[0]?.pretitle)}`,
          },
          buttonTwo: {
            label: sections?.[1]?.pretitle,
            link: `#${encodeURIComponent(sections?.[1]?.pretitle)}`,
          },
        }}
        breadCrumb={title}
        variant="treatment"
      />
      <Section className="flex flex-col lg:flex-row items-start gap-12 lg:gap-32">
        <SectionIndex
          sections={[
            ...(sections ?? []),
            ...(beforeAfter?.pretitle ? [{ pretitle: beforeAfter.pretitle }] : []),
            { pretitle: "FAQs" },
          ]}
        />
        <div className="[&>section]:py-16 flex-1">
          <Overview overview={overview} />
          {sections?.map((section, index) => (
            <DetailSection key={index} section={section} hasBackground={index % 2 === 1} />
          ))}
          <BeforeAfter beforeAfter={beforeAfter} hasBackground={(sections?.length ?? 0) % 2 === 1} />
        </div>
      </Section>
      <FAQs faqs={faqs} className="!mt-0" />
      {cta && <CallToAction variant="dark" {...cta} />}
    </Page>
  );
}

function SectionIndex({ sections }) {
  return (
    <div className="hidden lg:flex flex-col gap-4 flex-shrink-0 w-screen -ml-7 lg:ml-0 lg:w-[240px] sticky top-0 lg:top-4 ">
      <LogoEye className="z-0 w-12 h-12 text-primary hidden lg:block" />
      <nav className="sticky top-4 border-y border-dark/50 py-4 w-full overflow-auto bg-white lg:bg-transparent">
        <ul className="flex flex-row lg:flex-col items-center lg:items-start gap-8 lg:gap-4">
          {sections?.map((section, index) => (
            <li key={index} data-tina-field={tinaField(sections[index], `pretitle`)}>
              <ScrollLink
                className="text-dark/50 font-semibold line-clamp-1 cursor-pointer"
                activeClass="text-primary"
                to={encodeURIComponent(section.pretitle)}
                spy={true}
                offset={0}
                duration={100} // On Mobile -50
                // onSetActive={() => console.log("active")}
                // smooth={true}
                // smooth="easeOutQuad"
                // On active scroll horizontally to the element
                // onSetActive={(e) => scrollToActive(e.target)}
              >
                {section.pretitle}
              </ScrollLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

function Overview({ overview }) {
  return (
    <section className="bg-primary text-white rounded-lg !p-8 space-y-5 flex-1" id="Overview">
      <h2 className="h4 pb-3 border-b border-white/30">Overview</h2>
      <ul className="pl-4 grid lg:grid-cols-2 gap-x-12 gap-y-4 list-disc marker:text-white">
        {overview?.points?.map((pointItem, index) => (
          <li key={index} className="" data-tina-field={tinaField(overview, `points.${index}.point`)}>
            {pointItem.point}
          </li>
        ))}
      </ul>
    </section>
  );
}

function DetailSection({ section, hasBackground }) {
  return (
    <section
      className={cn("space-y-6", hasBackground && "gray-section-background")}
      id={encodeURIComponent(section.pretitle)}
    >
      <hgroup className="border-b border-dark/50 pb-3 space-y-2">
        <Breadcrumb label={section.pretitle} data-tina-field={tinaField(section, "pretitle")} size="sm" />
        <h2 className="h2" data-tina-field={tinaField(section, "title")}>
          {section.title}
        </h2>
      </hgroup>
      <div className="space-y-8">
        {section?.subSections?.map((subSection, index) => (
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-6" key={index}>
            <div
              className="flex-1 font-bold text-[17px]"
              data-tina-field={tinaField(section, `subSections.${index}.title`)}
            >
              {subSection.title}
            </div>
            <div className="flex-[3]">
              {hasMarkdownContent(subSection?.description) && (
                <div data-tina-field={tinaField(section, `subSections.${index}.description`)} className="mb-4">
                  <Markdown content={subSection?.description} />
                </div>
              )}

              {subSection?.points?.length > 0 && (
                <div className={cn(hasMarkdownContent(subSection?.description) && "pt-3 mt-3 border-t border-dark/30")}>
                  {subSection?.points?.map((pointItem, pointIndex) => (
                    <div
                      key={pointIndex}
                      className="flex flex-col gap-1 py-3 border-b border-dark/30 first:pt-0 last:border-0"
                    >
                      <div
                        className="font-bold text-[17px]"
                        data-tina-field={tinaField(section, `subSections.${index}.points.${pointIndex}.title`)}
                      >
                        {pointItem.title}
                      </div>

                      <div
                        data-tina-field={tinaField(section, `subSections.${index}.points.${pointIndex}.description`)}
                      >
                        <Markdown content={pointItem.description} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export const getStaticPaths = async () => {
  const { data } = await tinaClient.queries.treatmentConnection();

  const paths = data.treatmentConnection.edges.map((x) => {
    return { params: { slug: x.node._sys.filename } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export const getStaticProps = async (ctx) => {
  const treatmentQuery = await tinaClient.queries.treatment({
    relativePath: ctx.params.slug + ".mdx",
  });

  return {
    props: {
      treatmentQuery,
    },
  };
};
